import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import moment from 'moment';
import Papa from 'papaparse';
import CoreHttpHandler from 'http/services/CoreHttpHandler';

const DownloadsContext = createContext(null);

export const useDownloads = () => useContext(DownloadsContext);

// Keep only the requested columns (by header name) in a CSV string.
// Matching is tolerant of case / spaces / underscores. If nothing matches,
// the original CSV is returned unchanged so the export never breaks.
const filterCsvColumns = (csvString, columns) => {
    if (!columns || !columns.length || typeof csvString !== 'string') return csvString;
    try {
        const parsed = Papa.parse(csvString, { header: true, skipEmptyLines: false });
        const headers = parsed.meta?.fields || [];
        if (!headers.length) return csvString;

        const norm = s => String(s).toLowerCase().replace(/[\s_]+/g, '');
        const keep = [];
        columns.forEach(col => {
            const match = headers.find(h => norm(h) === norm(col));
            if (match && !keep.includes(match)) keep.push(match);
        });

        if (!keep.length) return csvString;

        const rows = (parsed.data || []).filter(r => r && Object.keys(r).length > 0);
        const filtered = rows.map(r => {
            const o = {};
            keep.forEach(h => {
                o[h] = r[h] ?? '';
            });
            return o;
        });

        return Papa.unparse(filtered, { columns: keep });
    } catch (e) {
        console.error('CSV column filter failed:', e);
        return csvString;
    }
};

export const DownloadsProvider = ({ children }) => {
    const [downloads, setDownloads] = useState([]);
    const [isExporting, setIsExporting] = useState(false);
    const downloadIdRef = useRef(0);

    const addDownload = useCallback((fileName, status = 'pending') => {
        downloadIdRef.current += 1;
        const newDownload = {
            id: downloadIdRef.current,
            fileName,
            status,
            progress: 0,
            createdAt: Date.now(),
        };
        setDownloads(prev => [...prev, newDownload]);
        return downloadIdRef.current;
    }, []);

    const updateDownload = useCallback((id, updates) => {
        setDownloads(prev => prev.map(d => d.id === id ? { ...d, ...updates } : d));
    }, []);

    const clearCompletedDownloads = useCallback(() => {
        setDownloads(prev => prev.filter(d => d.status !== 'completed'));
    }, []);

    const clearAllDownloads = useCallback(() => {
        setDownloads([]);
    }, []);

    // Execute export - runs at app level so it won't stop on page change
    const executeExport = useCallback(async ({
        totalItems,
        ouBoxSearch,
        dateRange,
        statusType,
        selectedFailedCodeValue,
        outboxType,
        clientId,
        exportColumns,
        onSuccess,
        onError,
        onProgress
    }) => {
        const limit = 100000;
        const totalNoOfPages = Math.ceil(totalItems / limit) || 1;
        setIsExporting(true);

        const { campaignId, number, templateId } = ouBoxSearch;
        const S = dateRange.startDate;
        const E = dateRange.endDate;

        if (!S || !E) {
            setIsExporting(false);
            return;
        }

        // Create download entries for all pages
        const downloadIds = [];
        const startDateStr = moment(S).format('YYYY-MM-DD');
        const endDateStr = moment(E).format('YYYY-MM-DD');

        for (let page = 1; page <= totalNoOfPages; page++) {
            const fileName = totalNoOfPages === 1
                ? `${startDateStr}-${endDateStr}-outbox.csv`
                : `${startDateStr}-${endDateStr}-outbox-part${page}.csv`;
            const id = addDownload(fileName, 'pending');
            downloadIds.push({ page, downloadId: id, fileName });
        }

        try {
            for (let i = 0; i < downloadIds.length; i++) {
                const { page, downloadId, fileName } = downloadIds[i];

                updateDownload(downloadId, { status: 'downloading', progress: 0 });

                onProgress?.(`Exporting file ${page} of ${totalNoOfPages}`);

                updateDownload(downloadId, { progress: 30 });

                const response = await CoreHttpHandler.request(
                    'templateMessagesMFMS',
                    'exportOutbox',
                    {
                        page,
                        limit,
                        ...(number ? { number } : {}),
                        ...(campaignId ? { campaign_id: campaignId } : {}),
                        ...(templateId ? { templateId } : {}),
                        ...(statusType ? { status: statusType } : {}),
                        ...(selectedFailedCodeValue ? { failed_code: selectedFailedCodeValue } : {}),
                        start_date: S,
                        end_date: E,
                        recipientId: outboxType,
                        ...(clientId ? { clientId } : {})
                    },
                    null,
                    null,
                    true
                );

                updateDownload(downloadId, { progress: 70 });

                let csvData = response?.data || response;

                if (typeof csvData !== 'string') {
                    updateDownload(downloadId, { status: 'failed', progress: 0 });
                    throw new Error(`Invalid CSV response on page ${page}`);
                }

                // Keep only the requested columns when a column set is provided.
                csvData = filterCsvColumns(csvData, exportColumns);

                updateDownload(downloadId, { progress: 90 });

                const blob = new Blob([csvData], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);

                updateDownload(downloadId, { status: 'completed', progress: 100 });
            }

            onSuccess?.('All files exported successfully');
        } catch (error) {
            console.error('Export error:', error);
            onError?.(error.message || 'Unknown error');

            // Mark remaining downloads as failed
            downloadIds.forEach(({ downloadId }) => {
                setDownloads(prev =>
                    prev.map(d =>
                        d.id === downloadId && d.status !== 'completed'
                            ? { ...d, status: 'failed' }
                            : d
                    )
                );
            });
        } finally {
            setIsExporting(false);
        }
    }, [addDownload, updateDownload]);

    const isExportDownloading = downloads.some(d => d.status === 'pending' || d.status === 'downloading');

    return (
        <DownloadsContext.Provider value={{
            downloads,
            isExporting,
            isExportDownloading,
            addDownload,
            updateDownload,
            clearCompletedDownloads,
            clearAllDownloads,
            executeExport
        }}>
            {children}
        </DownloadsContext.Provider>
    );
};

export default DownloadsContext;